import { useCallback, useEffect, useRef, useState } from 'react';
import type { CameraSettings } from '../../types';
import {
  createCameraFrameMetadata,
  type CameraSize,
  type CapturedCameraFrame,
} from './cameraGeometry';

export function useCamera(settings: CameraSettings) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const sessionRef = useRef(0);
  const [status, setStatus] = useState<'loading' | 'ready' | 'denied' | 'unsupported' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);
  const [videoSize, setVideoSize] = useState<CameraSize | null>(null);

  const stop = useCallback(() => {
    sessionRef.current += 1;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    const video = videoRef.current;
    if (video) {
      video.onloadedmetadata = null;
      video.onresize = null;
      video.srcObject = null;
    }
    setAspectRatio(null);
    setVideoSize(null);
  }, []);

  const start = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus('unsupported');
      setError('此瀏覽器不支援相機功能。');
      return;
    }
    stop();
    const session = ++sessionRef.current;
    setStatus('loading');
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: settings.facingMode },
          // Ask for detail, not a shape. Supplying width and height together can
          // make Chromium crop-and-scale a landscape webcam into a portrait
          // stream before our own viewport geometry ever sees it.
          width: { ideal: 1920 },
        },
      });
      if (sessionRef.current !== session) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;
      const video = videoRef.current;
      if (video) {
        const updateVideoSize = () => {
          if (video.videoWidth <= 0 || video.videoHeight <= 0) return;
          setAspectRatio(video.videoWidth / video.videoHeight);
          setVideoSize({ width: video.videoWidth, height: video.videoHeight });
        };
        video.srcObject = stream;
        video.onloadedmetadata = updateVideoSize;
        video.onresize = updateVideoSize;
        await video.play();
        if (sessionRef.current !== session) return;
        updateVideoSize();
      }
      setStatus('ready');
    } catch (cause) {
      if (sessionRef.current !== session) return;
      const denied = cause instanceof DOMException && (cause.name === 'NotAllowedError' || cause.name === 'PermissionDeniedError');
      setStatus(denied ? 'denied' : 'error');
      setError(denied ? '請至瀏覽器設定允許 G-Camera 使用相機。' : '相機啟動失敗，請確認沒有其他程式正在使用鏡頭。');
    }
  }, [settings.facingMode, stop]);

  useEffect(() => {
    void start();
    return stop;
  }, [start, stop]);

  const captureFrame = useCallback(async (): Promise<CapturedCameraFrame> => {
    const video = videoRef.current;
    if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) throw new Error('相機尚未準備完成');
    const frame = video.closest<HTMLElement>('.camera-viewport');
    const sourceSize = { width: video.videoWidth, height: video.videoHeight };
    const viewportSize = frame && frame.clientWidth > 0 && frame.clientHeight > 0
      ? { width: frame.clientWidth, height: frame.clientHeight }
      : { width: video.clientWidth || video.videoWidth, height: video.clientHeight || video.videoHeight };
    const mirrored = settings.facingMode === 'user' && settings.mirrorPreview;
    const metadata = createCameraFrameMetadata(sourceSize, viewportSize, mirrored);
    const { sourceCrop, outputSize } = metadata;
    const canvas = document.createElement('canvas');
    canvas.width = outputSize.width;
    canvas.height = outputSize.height;
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) throw new Error('無法建立拍攝畫布');
    if (mirrored) {
      context.translate(canvas.width, 0);
      context.scale(-1, 1);
    }
    context.drawImage(
      video,
      sourceCrop.x,
      sourceCrop.y,
      sourceCrop.width,
      sourceCrop.height,
      0,
      0,
      canvas.width,
      canvas.height,
    );
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (result) => result ? resolve(result) : reject(new Error('照片輸出失敗')),
        'image/jpeg',
        0.94,
      );
    });
    return { blob, metadata };
  }, [settings.facingMode, settings.mirrorPreview]);

  const capture = useCallback(async (): Promise<Blob> => (await captureFrame()).blob, [captureFrame]);

  return {
    videoRef,
    streamRef,
    status,
    error,
    aspectRatio,
    videoSize,
    retry: start,
    capture,
    captureFrame,
  };
}
